import { Component, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { AuthService } from '../../shared/services/auth.service';

@Component({
  selector: 'app-login',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatIconModule
  ],
  templateUrl: './login.html',
  styleUrl: './login.scss'
})
export class Login {
  loginForm: FormGroup;
  forgotPasswordForm: FormGroup;
  errorMessage = signal<string | null>(null);
  isLoading = signal<boolean>(false);
  isForgotPasswordView = signal<boolean>(false);
  resetEmailSent = signal<boolean>(false);
  hidePassword = signal<boolean>(true);

  constructor(
    private readonly fb: FormBuilder,
    private readonly authService: AuthService,
    private readonly router: Router
  ) {
    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(6)]]
    });

    this.forgotPasswordForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]]
    });
  }

  async onSubmit(): Promise<void> {
    if (this.loginForm.invalid) {
      return;
    }

    this.isLoading.set(true);
    this.errorMessage.set(null);

    const { email, password } = this.loginForm.value;

    try {
      await this.authService.signIn(email, password);
      // Successful sign-in, redirect to dashboard/home
      await this.router.navigate(['/']);
    } catch (error: any) {
      console.error('Login error:', error);
      this.errorMessage.set(error.message || 'Authentication failed. Please check your credentials.');
    } finally {
      this.isLoading.set(false);
    }
  }

  switchToForgotPassword(): void {
    this.isForgotPasswordView.set(true);
    this.resetEmailSent.set(false);
    this.errorMessage.set(null);
    const currentEmail = this.loginForm.get('email')?.value;
    if (currentEmail) {
      this.forgotPasswordForm.get('email')?.setValue(currentEmail);
    }
  }

  switchToLogin(): void {
    this.isForgotPasswordView.set(false);
    this.errorMessage.set(null);
    const currentEmail = this.forgotPasswordForm.get('email')?.value;
    if (currentEmail) {
      this.loginForm.get('email')?.setValue(currentEmail);
    }
  }

  async onForgotPasswordSubmit(): Promise<void> {
    if (this.forgotPasswordForm.invalid) {
      return;
    }

    this.isLoading.set(true);
    this.errorMessage.set(null);

    const email = this.forgotPasswordForm.get('email')?.value;

    try {
      await this.authService.sendPasswordResetEmail(email);
      this.resetEmailSent.set(true);
    } catch (error: any) {
      console.error('Password reset error:', error);
      let errorMsg = 'Impossibile inviare l\'email di ripristino della password.';
      if (error.code === 'auth/user-not-found') {
        errorMsg = 'Nessun utente trovato con questo indirizzo email.';
      } else if (error.code === 'auth/invalid-email') {
        errorMsg = 'L\'indirizzo email inserito non è valido.';
      } else {
        errorMsg = error.message || errorMsg;
      }
      this.errorMessage.set(errorMsg);
    } finally {
      this.isLoading.set(false);
    }
  }
}
